/**
 * Conversation API Client
 * Communicates directly with the conversation service API
 */

import type { SenderKind } from './agent-analytics-client';
import { appendBrandIds } from '@/lib/brand-filter-params';
import { lineRequiredErrorFor } from '@/lib/line-required';
import type { SellChannel } from '@/types/sell-channel';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_TOKEN = import.meta.env.VITE_API_TOKEN;

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

// Auth error event for global handling
export const AUTH_ERROR_EVENT = 'auth:unauthorized';

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const url = `${API_BASE_URL}${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include', // Include httpOnly cookies
    headers: {
      'Content-Type': 'application/json',
      ...(API_TOKEN && { Authorization: `Bearer ${API_TOKEN}` }),
      ...headers,
    },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  // Handle authentication errors globally
  if (response.status === 401 || response.status === 403) {
    console.error('Authentication error from API:', response.status);
    // Dispatch custom event to notify auth system
    window.dispatchEvent(
      new CustomEvent(AUTH_ERROR_EVENT, {
        detail: { status: response.status },
      }),
    );
    const errorText = await response.text();
    throw new Error(`Unauthorized (${response.status}): ${errorText}`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    // 404 LINE_REQUIRED is not a failure of this app: the tenant has no WhatsApp line yet.
    // Typed so templates and the composer can say so once instead of toasting every request.
    const lineRequired = lineRequiredErrorFor(response.status, errorText);
    if (lineRequired) throw lineRequired;
    throw new Error(`API error (${response.status}): ${errorText}`);
  }

  return response.json();
}

/**
 * The brand/branch a row belongs to. Sent by the backend on every aggregate-aware
 * list and detail; absent for line sessions, which have exactly one brand.
 */
export interface ScopeLabels {
  brandId?: string;
  brandName?: string;
  locationId?: string | null;
  locationName?: string | null;
  /**
   * The chat arrived on a sucursal's own line whose sucursal is NOT linked to a location: the line
   * is campaigns-only and the agent does not answer there until the link is made in the dashboard.
   */
  lineUnlinked?: boolean;
}

// Types based on Conversation API spec
export interface ConversationListItem extends ScopeLabels {
  id: string;
  customerPhone: string;
  connectedPhone: string;
  connectionExternalId: string;
  customerName?: string;
  lastMessageAt: string;
  lastReadAt?: string;
  aiDisabledUntil?: string;
  handoffUntil?: string;
  /** Location (sucursal) this conversation currently belongs to — the last one the customer used. */
  preferredLocationId?: string | null;
  /** Short preview of the latest message (either direction), for the inbox row. */
  lastMessagePreview?: string | null;
  /** A DRAFT order is open on this conversation right now (derived live, never stale). */
  hasActiveOrder?: boolean;
  /**
   * Opened by the dev test endpoint (`/agent/test`), not by a real customer. Derived server-side
   * from `connectionExternalId` and sent only when true. A test thread is NOT a sandbox one — it is
   * real-scoped and listed among real chats — so this flag is the only thing that tells them apart.
   */
  isTest?: boolean;
  createdAt: string;
  updatedAt: string;
}

export type ConversationMessageType =
  'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'sticker' | 'interactive';

/** A reaction applied to a message, keyed by who reacted (stored in externalMetadata.reactions). */
export interface MessageReaction {
  emoji: string;
  at: string;
}

/** A message's `externalMetadata.reactions`; `business.externalId` is the provider id of our own reaction. */
export interface MessageReactions {
  customer?: MessageReaction;
  business?: MessageReaction & { externalId?: string };
}

/** The emojis an operator can react with from the inbox. The backend rejects anything else. */
export const OPERATOR_REACTION_EMOJIS = ['❤️', '👍', '😂', '😮', '🙏', '✅'] as const;

/**
 * externalMetadata is passed through raw from the backend. Known fields per message kind
 * (all optional — older messages may lack them):
 * - media: { id, url, mimeType, sha256, caption?, filename? }
 * - location: { latitude, longitude, name?, address? }
 * - interactiveType: 'button_reply' | 'list_reply' | 'nfm_reply' (incoming interactive subtype)
 * - listReply: { id, title, description? }
 * - nfmReply: { name?, body?, responseJson?, responseJsonRaw? } (flow completion payload)
 * - contacts: Array<{ name?, phones: Array<{ phone, type? }>, emails: string[] }>
 * - reactions: { customer?: MessageReaction, business?: MessageReaction }
 * - type: 'flow' | 'template' | 'location_request_message' (outgoing subtype discriminator)
 * - buttons / button / bodyText / ctaText / flowId / kind / templateId / params / mediaUrl
 * - deliveryError: { code?, reason? } (outgoing, set by a provider `failed` status)
 * - sendFailed / sendError (outgoing, the send itself never reached the provider)
 */
export type ConversationMessageMetadata = Record<string, unknown> & {
  media?: { id?: string; url?: string; mimeType?: string; caption?: string; filename?: string };
  location?: { latitude: number; longitude: number; name?: string; address?: string };
  interactiveType?: string;
  listReply?: { id?: string; title?: string; description?: string };
  nfmReply?: { name?: string; body?: string; responseJson?: unknown; responseJsonRaw?: string };
  contacts?: Array<{ name?: string; phones?: Array<{ phone?: string; type?: string }>; emails?: string[] }>;
  reactions?: { customer?: MessageReaction; business?: MessageReaction };
  type?: string;
  buttons?: Array<{ id: string; title: string }>;
  button?: { text?: string; actionUrl?: string; url?: string };
  bodyText?: string;
  ctaText?: string;
  templateId?: string;
  params?: unknown[];
  mediaUrl?: string;
  deliveryError?: { code?: string; reason?: string };
  sendError?: string;
  /**
   * The message an incoming row quotes (WhatsApp "reply to"): `id` is WhatsApp's id, `gsId` Gupshup's.
   * Our outgoing rows are keyed by whichever id the provider had reported when they were stored, so a
   * lookup tries both.
   */
  context?: { from?: string; id?: string; gsId?: string };
  /** The quoted message's id as the backend recorded it (Gupshup's when present, else WhatsApp's). */
  replyToExternalId?: string;
};

export interface ConversationMessage {
  id: string;
  conversationId: string;
  role: 'human' | 'assistant';
  content: string;
  type: ConversationMessageType;
  externalId: string;
  externalMetadata?: ConversationMessageMetadata;
  direction: 'incoming' | 'outgoing';
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  /** Who sent an outgoing message; null on incoming rows and on rows sent before it was recorded. */
  senderKind?: SenderKind | null;
  createdAt: string;
  updatedAt: string;
}

export type ConversationEventVisibility = 'default' | 'advanced';

export type ConversationEventActor = 'agent' | 'customer' | 'operator' | 'system';

export type ConversationEventType =
  | 'order_created'
  | 'order_placed'
  | 'order_paid'
  | 'order_payment_failed'
  | 'takeover_started'
  | 'handoff_requested'
  | 'ai_resumed'
  | 'flow_sent'
  | 'flow_completed'
  | 'agent_tool_called'
  | 'flow_screen_navigated'
  | 'tags_changed'
  | 'sentiment_changed'
  | 'item_added'
  | 'item_removed'
  | 'delivery_address_set'
  | 'payment_method_selected'
  | 'location_auto_selected'
  | 'branch_pinned'
  | 'bank_transfer_received'
  | 'transfer_manually_verified'
  | 'cart_edited_by_operator'
  | 'coupon_applied'
  | 'coupon_removed'
  | 'web_checkout_link_sent'
  | 'web_checkout_opened'
  | 'cart_superseded_by_web';

export type WhatsappFlowKind = 'menu' | 'fulfillment';

export type OrderPaymentMethod = 'payment_link' | 'saved_card';

export interface OrderCreatedEventPayload {
  /** The operator placed it over the panel's "ask the customer first" advice. */
  confirmedWithoutCustomerApproval?: boolean;
  orderId: string;
  total: number;
  deliveryType?: string;
  /** Which surface confirmed the draft; absent = the AI agent / operator panel. */
  source?: OrderEventSource;
}

/**
 * A `WEB` / `EXTERNAL_AGENT` order was placed and this conversation was created or attached at that
 * moment (D5/D25). The conversation exists *from this row onward* — there is no chat history before
 * it, and the placement itself is the only thing the customer did on WhatsApp.
 */
export interface OrderPlacedEventPayload {
  orderId: string;
  /** The surface the order was authored on. `WHATSAPP` never produces this row in practice. */
  channel: SellChannel;
}

export interface OrderPaidEventPayload {
  orderId: string;
  paymentId: string;
  method: OrderPaymentMethod;
}

export interface OrderPaymentFailedEventPayload {
  orderId: string;
  paymentId: string | null;
  method: OrderPaymentMethod;
}

export interface TakeoverStartedEventPayload {
  /** ISO-8601 timestamp */
  disabledUntil: string;
  takeoverMinutes: number;
}

export interface HandoffRequestedEventPayload {
  /** ISO-8601 timestamp */
  handoffUntil: string;
  /** The agent's internal summary of WHY it handed off, shown to the operator. Absent on older rows. */
  reason?: string;
}

export interface FlowSentEventPayload {
  kind: WhatsappFlowKind;
  flowId: string;
}

export interface FlowCompletedEventPayload {
  flowName?: string;
}

export interface AgentToolCalledEventPayload {
  tool: string;
  /** Truncated tool input */
  input: string;
  status: 'ok' | 'error';
  durationMs: number;
}

export interface FlowScreenNavigatedEventPayload {
  action: string;
  screen?: string;
  nextScreen?: string;
  kind: WhatsappFlowKind;
}

/** Retired: no longer emitted, kept so stored history still renders. */
export interface TagsChangedEventPayload {
  tagIds: string[];
}

/** Retired: no longer emitted, kept so stored history still renders. */
export interface SentimentChangedEventPayload {
  sentiment: string;
  previous: string | null;
}

export interface CartItemRef {
  catalogItemId: string;
  name: string;
  quantity: number;
}

/** Absent = the AI messaging agent; `flow` = a WhatsApp Flow (an in-device cart edit); `web` = the web checkout page. */
export type OrderEventSource = 'flow' | 'web';

export interface ItemAddedEventPayload {
  items: CartItemRef[];
  /** Human-readable reasons some requested items were skipped. */
  failedItems?: string[];
  source?: OrderEventSource;
}

export interface ItemRemovedEventPayload {
  catalogItemId: string;
  name?: string;
  /** Units removed when a partial quantity was taken off the line (absent = whole line removed). */
  quantity?: number;
  source?: OrderEventSource;
}

export type DeliveryAddressOutcome =
  'confirmed' | 'needs_confirmation' | 'out_of_zone' | 'coordinates_required' | 'unverified' | 'delivery_not_available';

export interface DeliveryAddressSetEventPayload {
  outcome: DeliveryAddressOutcome;
  /** `pre_order` = check_delivery (no draft yet); `in_order` = set_order_details. */
  stage: 'pre_order' | 'in_order';
  address?: string;
  locationId?: string;
  locationName?: string;
  fee?: number;
  currency?: string;
  source?: OrderEventSource;
}

export interface PaymentMethodSelectedEventPayload {
  method: string;
  savedCard?: boolean;
  savedCardLabel?: string;
  source?: OrderEventSource;
}

export interface CouponAppliedEventPayload {
  code: string;
  discountId: string;
  /** What it took off the cart at the moment it was applied. Can be 0 (reward not in the cart yet). */
  discountAmount: number;
  autoAddedItems?: CartItemRef[];
  source?: OrderEventSource;
}

export interface CouponRemovedEventPayload {
  code: string;
  source?: OrderEventSource;
}

/** The agent sent a web-checkout handoff link (the web surface's counterpart of flow_sent). */
export interface WebCheckoutLinkSentEventPayload {
  locationId?: string;
}

/** The customer opened the web checkout page — one row per session, not per refresh. */
export interface WebCheckoutOpenedEventPayload {
  locationId?: string;
  locationName?: string;
}

/**
 * The cart this conversation was building was REPLACED by the one the customer had put together on
 * the website, when they identified themselves there (D17). The web cart wins by decision, so this
 * is the timeline's only explanation for a cart that left the operator's panel with nobody touching
 * it.
 */
export interface CartSupersededByWebEventPayload {
  /** The chat cart that was retired — now `superseded`. */
  orderId: string;
  /** The web cart that won. */
  supersededByOrderId: string;
  /**
   * Did the customer get the "we replaced your cart" message? False when the send was refused or the
   * line could not be resolved — the swap still happened and the customer was not told.
   */
  noticeSent: boolean;
  /** Why the customer was not told. Present only when `noticeSent` is false. */
  reason?: SupersededNoticeSkipReason;
}

/**
 * Why the "we replaced your cart" message did not go out.
 *
 * - `handoff` — the thread is paused for a human, so the swap is recorded silently: the operator
 *   standing in front of it says it, not the bot.
 * - `no_conversation` — the conversation carries no reachable phone.
 * - `no_line` — the brand/branch has no WhatsApp line to write on.
 * - `send_refused` — the provider took the request and refused it (out of session window, …).
 * - `send_failed` — the send threw.
 */
export type SupersededNoticeSkipReason = 'handoff' | 'no_conversation' | 'no_line' | 'send_refused' | 'send_failed';

/**
 * The system picked the sucursal deterministically instead of asking the customer (e.g. it was the
 * only one open). Not persisted as a preference — this event is the only trace.
 */
export interface LocationAutoSelectedEventPayload {
  locationId: string;
  locationName?: string;
  reason: 'only_open_location';
  /** The tool whose send used the auto-selected location (e.g. `show_full_menu`). */
  tool?: string;
}

/**
 * The branch was settled at fulfilment: the confirmed delivery address picked the serving branch,
 * or the customer chose one for pickup. On a shared-menu brand the draft carries no branch until
 * this happens, so this row is the timeline's answer to "when did this order become that branch's?".
 *
 * Moving a cart to a branch re-resolves every line against that branch's catalog: `unservableCount`
 * lines are not on it at all and `repricedCount` cost something else there. Both are 0 on the
 * ordinary move, and are only worth showing when they are not.
 */
export interface BranchPinnedEventPayload {
  locationId: string;
  locationName: string;
  /**
   * What settled it: the delivery address resolved to this branch, the customer picked it for
   * pickup, or the customer moved an order that already had a branch to this one.
   */
  source: 'address' | 'pickup_choice' | 'customer_choice';
  unservableCount: number;
  repricedCount: number;
}

/**
 * A bank-transfer receipt arrived and was recorded as a `bank_transfer` row — whatever it was
 * attributed to. The id is the pointer operators follow to the Bank Transfers page.
 */
export interface BankTransferReceivedEventPayload {
  bankTransferId: string;
  /** menteo_order | pos_order | unknown */
  source: string;
  /** pending | verified | mismatch */
  status: string;
  orderId?: string;
  posOrderId?: string;
  amount?: number;
  currency?: string;
}

/**
 * An operator confirmed (or un-confirmed) a bank transfer by hand. The timeline's answer to "why did
 * this order go through with no comprobante?".
 */
export interface TransferManuallyVerifiedEventPayload {
  bankTransferId: string;
  orderId: string;
  /** True when the operator UNDID the confirmation — the order needs a receipt again. */
  reverted?: boolean;
  /** The status the receipt reader had left, when there was a receipt at all. */
  previousStatus?: string;
  /** False when nothing was on file: the operator's word is the only evidence of payment. */
  hadReceipt: boolean;
  reason?: string;
}

export interface AiResumedEventPayload {
  withMessage: boolean;
  buttonCount?: number;
  /** The operator said nothing and the agent was queued to write the next message itself. */
  agentTurnQueued?: boolean;
}

/** Per-type payload contract — `undefined` marks events that carry no payload. */
export interface ConversationEventPayloadMap {
  order_created: OrderCreatedEventPayload;
  order_placed: OrderPlacedEventPayload;
  order_paid: OrderPaidEventPayload;
  order_payment_failed: OrderPaymentFailedEventPayload;
  takeover_started: TakeoverStartedEventPayload;
  handoff_requested: HandoffRequestedEventPayload;
  ai_resumed: AiResumedEventPayload;
  flow_sent: FlowSentEventPayload;
  flow_completed: FlowCompletedEventPayload;
  agent_tool_called: AgentToolCalledEventPayload;
  flow_screen_navigated: FlowScreenNavigatedEventPayload;
  tags_changed: TagsChangedEventPayload;
  sentiment_changed: SentimentChangedEventPayload;
  item_added: ItemAddedEventPayload;
  item_removed: ItemRemovedEventPayload;
  delivery_address_set: DeliveryAddressSetEventPayload;
  payment_method_selected: PaymentMethodSelectedEventPayload;
  location_auto_selected: LocationAutoSelectedEventPayload;
  branch_pinned: BranchPinnedEventPayload;
  bank_transfer_received: BankTransferReceivedEventPayload;
  transfer_manually_verified: TransferManuallyVerifiedEventPayload;
  cart_edited_by_operator: CartEditedByOperatorEventPayload;
  coupon_applied: CouponAppliedEventPayload;
  coupon_removed: CouponRemovedEventPayload;
  web_checkout_link_sent: WebCheckoutLinkSentEventPayload;
  web_checkout_opened: WebCheckoutOpenedEventPayload;
  cart_superseded_by_web: CartSupersededByWebEventPayload;
}

/** An operator saved a hand-edited cart from the inbox panel — one entry per save. */
export interface CartEditedByOperatorEventPayload {
  orderId: string;
  changed: ('items' | 'delivery' | 'payment' | 'notes' | 'tip' | 'location')[];
  itemCount: number;
  total: number;
  /** Absent on rows written before the diff was recorded — render nothing rather than "0". */
  before?: CartEditSnapshot;
  after?: CartEditSnapshot;
  requiresCustomerApproval?: boolean;
  approvalReasons?: string[];
}

export interface CartEditSnapshot {
  total: number;
  itemCount: number;
  deliveryType?: 'delivery' | 'pickup';
  paymentMethod?: string;
  address?: string;
}

interface ConversationEventBase {
  id: string;
  conversationId: string;
  visibility: ConversationEventVisibility;
  actor: ConversationEventActor;
  correlationId?: string;
  createdAt: string;
}

/**
 * Discriminated on `type` so `payload` narrows per event kind.
 * Forward compat: a newer server may emit types this build doesn't know — consumers must keep a
 * runtime fallback for unknown `type` values (icon default + `events.unknown` i18n label).
 */
export type ConversationEvent = {
  [T in ConversationEventType]: ConversationEventBase & { type: T; payload?: ConversationEventPayloadMap[T] };
}[ConversationEventType];

export interface GetConversationEventsResponse {
  success: boolean;
  message: string;
  data: ConversationEvent[];
  meta: {
    currentPage: number;
    itemsPerPage: number;
    itemCount: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface ListConversationsResponse {
  success: boolean;
  message: string;
  data: ConversationListItem[];
  meta: {
    currentPage: number;
    itemsPerPage: number;
    itemCount: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface GetConversationMessagesResponse {
  success: boolean;
  message: string;
  data: ConversationMessage[];
  meta: {
    currentPage: number;
    itemsPerPage: number;
    itemCount: number;
    totalItems: number;
    totalPages: number;
  };
}

/**
 * Outgoing message counts behind the composer's cost line (C7). Only delivered, non-template session
 * messages count. `monthStart` is the calendar month in the brand's timezone; `approximate` is always
 * true because Meta resets the free tier on the WhatsApp account's own timezone, which we don't store.
 */
export interface ConversationMessageStats {
  conversation: { sentThisMonth: number };
  line: {
    sentThisMonth: number;
    freeTierLimit: number;
    freeTierUsed: number;
    monthStart: string;
    approximate: boolean;
  };
}

export const conversationClient = {
  conversations: {
    async list(params?: {
      page?: number;
      limit?: number;
      filter?: 'manual' | 'handoff' | 'unread';
      locationId?: string;
      /** Channel scope; omitted = both (the unified inbox). */
      channel?: 'whatsapp' | 'instagram';
      /** Aggregate sessions only: narrow to these brands (omitted = every brand in scope). */
      brandIds?: string[];
    }) {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.filter) searchParams.set('filter', params.filter);
      if (params?.locationId) searchParams.set('locationId', params.locationId);
      if (params?.channel) searchParams.set('channel', params.channel);
      appendBrandIds(searchParams, params?.brandIds);

      return api<ListConversationsResponse>(`/conversations?${searchParams.toString()}`);
    },

    async getHandoffCount(params?: { brandIds?: string[] }) {
      const searchParams = new URLSearchParams();
      appendBrandIds(searchParams, params?.brandIds);
      const query = searchParams.toString();
      return api<{ count: number }>(`/conversations/handoff-count${query ? `?${query}` : ''}`);
    },

    async get(conversationId: string) {
      return api<{ success: boolean; data: ConversationListItem }>(`/conversations/${conversationId}`);
    },

    async getMessageStats(conversationId: string) {
      return api<{ success: boolean; message: string; data: ConversationMessageStats }>(
        `/conversations/${conversationId}/message-stats`,
      );
    },

    /**
     * Hand the conversation back to the agent, optionally with a closing message.
     *
     * `buttons` attaches WhatsApp quick replies to that message (max 3, 20 chars each). A tap comes
     * back as an ordinary customer message, so "Sí, confirmar" reaches the agent exactly like a
     * typed "sí" would — it is a shortcut for the customer, not a separate confirmation path.
     *
     * `requestAgentTurn` (only without a message) queues the agent to write the next message itself.
     * Resuming alone would not: the agent answers customer messages, and after a hand-back the last
     * word is the operator's, so the conversation would sit silent until the customer wrote again.
     *
     * The body is whitelisted server-side — anything not on `ResumeAiDto` is a 400, so don't invent
     * fields here. Both flags (aiDisabledUntil, handoffUntil) are always cleared.
     */
    async resumeAi(
      conversationId: string,
      options?: {
        message?: string;
        buttons?: { id: string; title: string }[];
        requestAgentTurn?: boolean;
      },
    ) {
      return api<{ success: boolean; agentTurnQueued: boolean }>(`/conversations/${conversationId}/resume-ai`, {
        method: 'POST',
        ...(options ? { body: options } : {}),
      });
    },

    /**
     * Pause the AI and hand the conversation to the operator, for the brand's takeover window.
     * Safe to call repeatedly — every call pushes the window forward, which is how an operator who
     * is still working never has the agent wake up underneath them.
     */
    async takeover(conversationId: string) {
      return api<{ success: boolean; aiDisabledUntil: string }>(`/conversations/${conversationId}/takeover`, {
        method: 'POST',
      });
    },
  },

  events: {
    /** Timeline events for a conversation (visibility 'all' includes advanced/tooling events). */
    async listByConversation(params: {
      conversationId: string;
      visibility?: 'default' | 'all';
      page?: number;
      limit?: number;
    }) {
      const searchParams = new URLSearchParams();
      if (params.visibility) searchParams.set('visibility', params.visibility);
      if (params.page) searchParams.set('page', params.page.toString());
      if (params.limit) searchParams.set('limit', params.limit.toString());

      const query = searchParams.toString();
      return api<GetConversationEventsResponse>(
        `/conversations/${params.conversationId}/events${query ? `?${query}` : ''}`,
      );
    },
  },

  /**
   * Every send takes an optional `conversationId`: it is what tells the backend WHICH
   * line to send from, and an aggregate session spans many lines, so there it is
   * required (the phone number alone is ambiguous across brands). Line sessions may
   * omit it — the backend falls back to the session's own line, as before.
   */
  messages: {
    async listByConversation(params: { conversationId: string; page?: number; limit?: number }) {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page.toString());
      if (params.limit) searchParams.set('limit', params.limit.toString());

      const query = searchParams.toString();
      return api<GetConversationMessagesResponse>(
        `/conversations/${params.conversationId}/chat-messages${query ? `?${query}` : ''}`,
      );
    },

    /**
     * React to a customer message as the business; `emoji: ''` removes our reaction. Reacting never
     * pauses the AI (no takeover). Returns the message's reactions as stored after the send.
     */
    async react(params: { conversationId: string; messageId: string; emoji: string }) {
      return api<{ success: boolean; message: string; data: { reactions: MessageReactions } }>(
        `/conversations/${params.conversationId}/messages/${params.messageId}/reaction`,
        { method: 'POST', body: { emoji: params.emoji } },
      );
    },

    async sendText(data: { to: string; body: string; role?: 'human' | 'assistant'; conversationId?: string }) {
      return api<{ success: boolean; message: string }>('/gupshup/send-session-message', {
        method: 'POST',
        body: {
          phoneNumber: data.to,
          ...(data.conversationId ? { conversationId: data.conversationId } : {}),
          message: {
            type: 'text',
            text: data.body,
          },
          ...(data.role ? { role: data.role } : {}),
        },
      });
    },

    async sendImage(data: { to: string; image: { link?: string; caption?: string }; conversationId?: string }) {
      return api<{ success: boolean; message: string }>('/gupshup/send-session-message', {
        method: 'POST',
        body: {
          phoneNumber: data.to,
          ...(data.conversationId ? { conversationId: data.conversationId } : {}),
          message: {
            type: 'image',
            originalUrl: data.image.link,
            caption: data.image.caption,
          },
        },
      });
    },

    async sendVideo(data: { to: string; video: { link?: string; caption?: string }; conversationId?: string }) {
      return api<{ success: boolean; message: string }>('/gupshup/send-session-message', {
        method: 'POST',
        body: {
          phoneNumber: data.to,
          ...(data.conversationId ? { conversationId: data.conversationId } : {}),
          message: {
            type: 'video',
            url: data.video.link,
            caption: data.video.caption,
          },
        },
      });
    },

    async sendAudio(data: { to: string; audio: { link?: string }; conversationId?: string }) {
      return api<{ success: boolean; message: string }>('/gupshup/send-session-message', {
        method: 'POST',
        body: {
          phoneNumber: data.to,
          ...(data.conversationId ? { conversationId: data.conversationId } : {}),
          message: {
            type: 'audio',
            url: data.audio.link,
          },
        },
      });
    },

    async sendDocument(data: {
      to: string;
      document: { link?: string; caption?: string; filename?: string };
      conversationId?: string;
    }) {
      return api<{ success: boolean; message: string }>('/gupshup/send-session-message', {
        method: 'POST',
        body: {
          phoneNumber: data.to,
          ...(data.conversationId ? { conversationId: data.conversationId } : {}),
          message: {
            type: 'file',
            url: data.document.link,
            filename: data.document.filename,
          },
        },
      });
    },

    async sendInteractiveButtons(data: {
      to: string;
      body: string;
      header?: string;
      buttons: Array<{ id: string; title: string }>;
      conversationId?: string;
    }) {
      return api<{ success: boolean; message: string }>('/gupshup/send-session-message', {
        method: 'POST',
        body: {
          phoneNumber: data.to,
          ...(data.conversationId ? { conversationId: data.conversationId } : {}),
          message: {
            type: 'interactive',
            text: data.body,
            ...(data.header ? { header: data.header } : {}),
            buttons: data.buttons,
          },
        },
      });
    },
  },

  templates: {
    /**
     * Templates are a per-LINE resource, so an aggregate session has to say which
     * conversation it is composing for; line sessions omit it and get their own line's.
     */
    async list(params?: { conversationId?: string }) {
      const query = params?.conversationId ? `?conversationId=${encodeURIComponent(params.conversationId)}` : '';
      return api<import('@/types/whatsapp').GupshupTemplatesResponse>(`/brands/templates${query}`);
    },

    async create(data: import('@/types/whatsapp').CreateGupshupTemplatePayload) {
      return api<{ status: string; message?: string }>('/brands/templates', {
        method: 'POST',
        body: data,
      });
    },

    async update(templateId: string, data: Partial<import('@/types/whatsapp').CreateGupshupTemplatePayload>) {
      return api<{ status: string; message?: string }>(`/brands/templates/${templateId}`, {
        method: 'PUT',
        body: data,
      });
    },

    async delete(elementName: string) {
      return api<{ status: string; message?: string }>(`/brands/templates/${encodeURIComponent(elementName)}`, {
        method: 'DELETE',
      });
    },

    /**
     * Uploads sample media to Gupshup and returns a handleId (in
     * `handleId.message`) to be used as `exampleMedia` on template creation.
     * Sent as multipart/form-data — the browser sets the boundary, so we must
     * not set Content-Type here.
     */
    async uploadMedia(file: Blob, fileType: import('@/types/whatsapp').TemplateMediaFileType, filename: string) {
      const form = new FormData();
      form.append('file_type', fileType);
      form.append('file', file, filename);

      const response = await fetch(`${API_BASE_URL}/brands/templates/media`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...(API_TOKEN ? { Authorization: `Bearer ${API_TOKEN}` } : {}),
        },
        body: form,
      });

      if (response.status === 401 || response.status === 403) {
        window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: { status: response.status } }));
        const errorText = await response.text();
        throw new Error(`Unauthorized (${response.status}): ${errorText}`);
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error (${response.status}): ${errorText}`);
      }

      return response.json() as Promise<import('@/types/whatsapp').GupshupUploadMediaResponse>;
    },

    async send(data: {
      phoneNumber: string;
      templateId: string;
      params?: string[];
      mediaUrl?: string;
      conversationId?: string;
    }) {
      return api<{ status: string; messageId?: string }>('/brands/templates/send', {
        method: 'POST',
        body: data,
      });
    },
  },

  phone: {
    async health() {
      return api<import('@/types/whatsapp').PhoneHealthResponse>('/brands/phone/health');
    },

    async qualityRating() {
      return api<import('@/types/whatsapp').QualityRatingResponse>('/brands/phone/quality-rating');
    },
  },

  media: {
    async get(params: { mediaId: string }) {
      // Media not supported yet
      return {
        id: params.mediaId,
        mimeType: 'application/octet-stream',
        fileSize: '0',
      };
    },

    async download() {
      throw new Error('Media download not supported');
    },

    async upload() {
      throw new Error('Media upload not supported - use direct URL instead');
    },
  },
};

// Export as whatsappClient for compatibility
export const gupshupClient = conversationClient;
